import { useCallback, useEffect, useState } from 'react';
import z from 'zod';
import { DATASETS_TABLE_STATE } from '../../../redux/modules/localStorage/actions';
import { parseAndValidate } from '../../../utils/jsonParse';

const entrySchema = z.object({
  columnOrder: z.array(z.string()).optional(),
  // Touched on mount. Recorded now so future pruning of stale entries
  // can use it without a schema migration.
  lastOpenedAt: z.number().optional(),
});

export type DatasetTableStateEntry = z.infer<typeof entrySchema>;

const stateSchema = z.record(entrySchema);

const readState = () =>
  parseAndValidate(localStorage.getItem(DATASETS_TABLE_STATE), stateSchema, {});

export const useDatasetTableState = (datasetName: string) => {
  const [state, setState] = useState(readState);

  useEffect(() => {
    setState(prev => ({
      ...prev,
      [datasetName]: { ...prev[datasetName], lastOpenedAt: Date.now() },
    }));
  }, [datasetName]);

  useEffect(() => {
    localStorage.setItem(DATASETS_TABLE_STATE, JSON.stringify(state));
  }, [state]);

  const updateEntry = useCallback(
    (patch: Partial<Omit<DatasetTableStateEntry, 'lastOpenedAt'>>) => {
      setState(prev => ({
        ...prev,
        [datasetName]: {
          ...prev[datasetName],
          ...patch,
          lastOpenedAt: Date.now(),
        },
      }));
    },
    [datasetName]
  );

  return { entry: state[datasetName], updateEntry };
};
